import { useCallback, useEffect, useState } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import {
  apiBase,
  withApiKey,
  insertPresetOntoCanvas,
  type PresetJson,
} from '../selectionTemplates';

type Category = { id: string; name: string };

type ElementItem = {
  id: string;
  title: string;
  categoryId: string;
  preview: string;
  json: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  store: RaeditorStore;
};

export function ManageElementsModal({ open, onClose, onStatus, store }: Props) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [elements, setElements] = useState<ElementItem[]>([]);
  const [filterCat, setFilterCat] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCatName, setNewCatName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const catQs = filterCat ? `?category=${encodeURIComponent(filterCat)}` : '';
      const [catRes, elRes] = await Promise.all([
        fetch(withApiKey(`${apiBase()}/api/element-categories`)),
        fetch(withApiKey(`${apiBase()}/api/element-templates${catQs}`)),
      ]);
      const catData = (await catRes.json().catch(() => ({}))) as {
        categories?: Category[];
        error?: string;
      };
      const elData = (await elRes.json().catch(() => ({}))) as {
        items?: ElementItem[];
        error?: string;
      };
      if (!catRes.ok) {
        setError(catData.error || `Categories failed (${catRes.status})`);
      } else {
        setCategories(catData.categories || []);
      }
      if (!elRes.ok) {
        setError(elData.error || `Elements failed (${elRes.status})`);
        setElements([]);
      } else {
        setElements(elData.items || []);
      }
    } catch (err) {
      console.error(err);
      setError('Could not reach element APIs');
      setElements([]);
    } finally {
      setLoading(false);
    }
  }, [filterCat]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setBusyId('cat-add');
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/element-categories`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        categories?: Category[];
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message || data.error || 'Add category failed');
        return;
      }
      setCategories(data.categories || []);
      setNewCatName('');
      onStatus(`Category “${name}” created`);
    } catch (err) {
      console.error(err);
      setError('Add category failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRenameCategory = async (id: string) => {
    const name = renameValue.trim();
    if (!name) return;
    setBusyId(`rename-${id}`);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/element-categories/${encodeURIComponent(id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        categories?: Category[];
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message || data.error || 'Rename failed');
        return;
      }
      setCategories(data.categories || []);
      setRenamingId(null);
      onStatus(`Category renamed to “${name}”`);
    } catch (err) {
      console.error(err);
      setError('Rename failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveCategory = async (cat: Category) => {
    if (cat.id === 'uncategorized') {
      onStatus('Cannot remove Uncategorized');
      return;
    }
    if (
      !window.confirm(
        `Remove category “${cat.name}”? Elements in it will move to Uncategorized.`,
      )
    ) {
      return;
    }
    setBusyId(`del-cat-${cat.id}`);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/element-categories/${encodeURIComponent(cat.id)}`),
        { method: 'DELETE' },
      );
      const data = (await res.json().catch(() => ({}))) as {
        categories?: Category[];
        moved?: number;
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message || data.error || 'Remove category failed');
        return;
      }
      setCategories(data.categories || []);
      if (filterCat === cat.id) setFilterCat('');
      onStatus(`Category removed — ${data.moved ?? 0} element(s) moved to Uncategorized`);
      void load();
    } catch (err) {
      console.error(err);
      setError('Remove category failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleInsert = async (item: ElementItem) => {
    setBusyId(`ins-${item.id}`);
    setError(null);
    try {
      const jsonUrl =
        item.json.startsWith('http') || item.json.startsWith('/')
          ? `${item.json.startsWith('/') ? apiBase() : ''}${item.json}`
          : `${apiBase()}/api/element-templates/${encodeURIComponent(item.id)}.json`;
      const res = await fetch(withApiKey(jsonUrl));
      if (!res.ok) {
        setError(`Failed to load element JSON (${res.status})`);
        return;
      }
      const preset = (await res.json()) as PresetJson;
      const ids = insertPresetOntoCanvas(store, preset);
      onStatus(
        ids.length
          ? `Inserted “${item.title}” (${ids.length} node${ids.length === 1 ? '' : 's'})`
          : `Inserted “${item.title}”`,
      );
    } catch (err) {
      console.error(err);
      setError('Insert failed');
      onStatus('Insert element failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeleteElement = async (item: ElementItem) => {
    const password = window.prompt(
      `Delete element “${item.title}”?\n\nEnter delete password:`,
    );
    if (password === null) return;
    if (!password.trim()) {
      onStatus('Delete cancelled — password required');
      return;
    }
    if (!window.confirm(`Permanently delete ${item.id}?`)) return;

    setBusyId(`del-${item.id}`);
    setError(null);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/element-templates/${encodeURIComponent(item.id)}`),
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-Delete-Password': password,
          },
          body: JSON.stringify({ password }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        const msg = data.message || data.error || `Delete failed (${res.status})`;
        setError(msg);
        onStatus(`Delete failed: ${msg}`);
        return;
      }
      setElements((prev) => prev.filter((x) => x.id !== item.id));
      onStatus(`Deleted element ${item.id}`);
    } catch (err) {
      console.error(err);
      setError('Delete request failed');
      onStatus('Delete element failed');
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  const titleId = 'manage-elements-title';
  const catName = (id: string) => categories.find((c) => c.id === id)?.name || id;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--wide modal--elements"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            My elements
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Browse saved element packs (mixed text, images, shapes). Click a card to insert onto the
          canvas. Manage categories below — removing a category moves its items to Uncategorized.
        </p>

        <div className="el-manage__cats">
          <div className="el-manage__cats-head">Categories</div>
          <ul className="el-manage__cat-list">
            {categories.map((cat) => (
              <li key={cat.id} className="el-manage__cat-row">
                {renamingId === cat.id ? (
                  <>
                    <input
                      className="modal__input el-manage__rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn btn--accent"
                      disabled={busyId === `rename-${cat.id}`}
                      onClick={() => void handleRenameCategory(cat.id)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      onClick={() => setRenamingId(null)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="el-manage__cat-name">{cat.name}</span>
                    <button
                      type="button"
                      className="btn btn--ghost-dark"
                      disabled={cat.id === 'uncategorized'}
                      onClick={() => {
                        setRenamingId(cat.id);
                        setRenameValue(cat.name);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={cat.id === 'uncategorized' || busyId === `del-cat-${cat.id}`}
                      onClick={() => void handleRemoveCategory(cat)}
                    >
                      Remove
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="el-manage__add-cat">
            <input
              className="modal__input"
              placeholder="New category name"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
            />
            <button
              type="button"
              className="btn btn--accent"
              disabled={!newCatName.trim() || busyId === 'cat-add'}
              onClick={() => void handleAddCategory()}
            >
              Add
            </button>
          </div>
        </div>

        <label className="modal__label" htmlFor="el-filter-cat">
          Filter by category
        </label>
        <select
          id="el-filter-cat"
          className="modal__input"
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        {loading && (
          <div className="modal__status" role="status">
            Loading…
          </div>
        )}
        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        {!loading && elements.length === 0 && !error && (
          <div className="modal__status">
            No saved elements yet. Select canvas items → Save element.
          </div>
        )}

        <div className="el-manage__grid">
          {elements.map((item) => (
            <div key={item.id} className="el-manage__card">
              <button
                type="button"
                className="el-manage__thumb"
                disabled={busyId === `ins-${item.id}`}
                onClick={() => void handleInsert(item)}
                title="Insert onto canvas"
              >
                <img src={withApiKey(`${apiBase()}${item.preview}`)} alt="" loading="lazy" />
              </button>
              <div className="el-manage__card-meta">
                <div className="el-manage__card-title">{item.title}</div>
                <div className="el-manage__card-cat">{catName(item.categoryId)}</div>
              </div>
              <div className="el-manage__card-actions">
                <button
                  type="button"
                  className="btn btn--accent"
                  disabled={busyId === `ins-${item.id}`}
                  onClick={() => void handleInsert(item)}
                >
                  {busyId === `ins-${item.id}` ? '…' : 'Insert'}
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  disabled={busyId === `del-${item.id}`}
                  onClick={() => void handleDeleteElement(item)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost-dark"
            onClick={() => void load()}
            disabled={loading}
          >
            Refresh
          </button>
          <button type="button" className="btn btn--accent" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
